/**
 * Soft Quality Gates
 * Non-blocking modals that nudge users to provide better structured feedback
 * before submission. Offers "Improve Now" or "Submit as-is" options.
 */

import type { FeedbackCategory } from './types';
import { escapeHtml } from './dom-utils';

export interface QualityGateCheck {
  pass: boolean;
  suggestions: string[];
}

/**
 * Check feedback quality and return suggestions for improvement.
 * Always returns pass: true (soft gate, never blocks).
 */
export function checkFeedbackQuality(fields: {
  category: FeedbackCategory;
  title: string;
  description: string;
  expectedBehavior?: string;
  stepsToReproduce?: string;
  successCriteria?: string;
  contextualInfo?: string;
}): QualityGateCheck {
  const suggestions: string[] = [];

  // Check core fields
  if (!fields.title || fields.title.trim().length < 10) {
    suggestions.push('Add a more descriptive title (10+ characters)');
  }

  if (!fields.description || fields.description.trim().length < 20) {
    suggestions.push('Add more detail to your description');
  }

  // Category-specific checks
  if (fields.category === 'bug') {
    if (!fields.expectedBehavior || fields.expectedBehavior.trim().length === 0) {
      suggestions.push('Describe what you expected to happen — this helps us understand the issue faster');
    }

    if (!fields.stepsToReproduce || fields.stepsToReproduce.trim().length === 0) {
      suggestions.push('Add step-by-step instructions to reproduce the issue');
    }
  } else if (fields.category === 'improvement') {
    if (!fields.successCriteria || fields.successCriteria.trim().length === 0) {
      suggestions.push('Describe what success looks like for this improvement');
    }
  } else if (fields.category === 'question') {
    if (!fields.contextualInfo || fields.contextualInfo.trim().length === 0) {
      suggestions.push('Add context about your use case or environment');
    }
  }

  return {
    pass: true, // Always soft gate — user can override
    suggestions,
  };
}

/**
 * Render a soft quality gate modal.
 * Offers "Improve Now" (go back to form) or "Submit as-is" (proceed with submission).
 */
export function renderQualityGateModal(options: {
  category: FeedbackCategory;
  suggestions: string[];
  onImproveNow: () => void;
  onSubmitAnyway: () => void;
}): HTMLDivElement {
  const modal = document.createElement('div');
  modal.className = 'fh-quality-gate-modal';
  modal.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.3);
    padding: 24px;
    max-width: 420px;
    z-index: 1000000;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  `;

  const overlay = document.createElement('div');
  overlay.className = 'fh-quality-gate-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    z-index: 999999;
  `;

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      options.onSubmitAnyway();
    }
  });

  // Header
  const header = document.createElement('div');
  header.style.cssText = `
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e5e7eb;
  `;
  header.innerHTML = `
    <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #1f2937">
      💡 A few quick improvements
    </h3>
  `;
  modal.appendChild(header);

  // Suggestions list
  const suggestionsList = document.createElement('ul');
  suggestionsList.style.cssText = `
    list-style: none;
    padding: 0;
    margin: 0 0 20px 0;
  `;

  for (const suggestion of options.suggestions) {
    const item = document.createElement('li');
    item.style.cssText = `
      padding: 8px 0;
      font-size: 14px;
      color: #4b5563;
      display: flex;
      align-items: flex-start;
      gap: 8px;
    `;
    item.innerHTML = `
      <span style="flex-shrink: 0; width: 20px; text-align: center; color: #f59e0b">→</span>
      <span>${escapeHtml(suggestion)}</span>
    `;
    suggestionsList.appendChild(item);
  }
  modal.appendChild(suggestionsList);

  // Message
  const message = document.createElement('p');
  message.style.cssText = `
    margin: 0 0 20px 0;
    font-size: 13px;
    color: #6b7280;
    line-height: 1.5;
  `;
  message.textContent =
    'Better structured feedback helps our team respond faster and more accurately.';
  modal.appendChild(message);

  // Buttons
  const buttonGroup = document.createElement('div');
  buttonGroup.style.cssText = `
    display: flex;
    gap: 12px;
  `;

  const improveBtn = document.createElement('button');
  improveBtn.className = 'fh-quality-gate-improve';
  improveBtn.textContent = '← Go Back & Improve';
  improveBtn.style.cssText = `
    flex: 1;
    padding: 10px 16px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    color: #374151;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 200ms;
  `;
  improveBtn.onmouseover = () => (improveBtn.style.background = '#f9fafb');
  improveBtn.onmouseout = () => (improveBtn.style.background = 'white');
  improveBtn.onclick = () => options.onImproveNow();

  const submitBtn = document.createElement('button');
  submitBtn.className = 'fh-quality-gate-submit';
  submitBtn.textContent = 'Submit Anyway →';
  submitBtn.style.cssText = `
    flex: 1;
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    background: #3b82f6;
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 200ms;
  `;
  submitBtn.onmouseover = () => (submitBtn.style.background = '#2563eb');
  submitBtn.onmouseout = () => (submitBtn.style.background = '#3b82f6');
  submitBtn.onclick = () => options.onSubmitAnyway();

  buttonGroup.appendChild(improveBtn);
  buttonGroup.appendChild(submitBtn);
  modal.appendChild(buttonGroup);

  // Mount modal and overlay
  document.body.appendChild(overlay);
  document.body.appendChild(modal);

  return overlay;
}

/**
 * Remove the quality gate modal and overlay from the DOM.
 */
export function dismissQualityGateModal(): void {
  const overlay = document.querySelector('.fh-quality-gate-overlay');
  const modal = document.querySelector('.fh-quality-gate-modal');
  overlay?.remove();
  modal?.remove();
}

/**
 * Render a quality gate inline within the widget container (replaces form content).
 * Used for v2 form to avoid a separate page-level popup.
 */
export function renderInlineQualityGate(options: {
  container: HTMLElement;
  suggestions: string[];
  onImproveNow: () => void;
  onSubmitAnyway: () => void;
}): void {
  const { container, suggestions, onImproveNow, onSubmitAnyway } = options;

  // Hide existing form content
  const existingChildren = Array.from(container.children) as HTMLElement[];
  for (const child of existingChildren) {
    child.style.display = 'none';
  }

  const wrapper = document.createElement('div');
  wrapper.className = 'fh-quality-gate-inline';
  wrapper.style.cssText = `
    padding: 16px;
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = `
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e5e7eb;
  `;
  header.innerHTML = `
    <h3 style="margin: 0; font-size: 15px; font-weight: 600; color: #1f2937">
      💡 A few quick improvements
    </h3>
  `;
  wrapper.appendChild(header);

  // Suggestions list
  const suggestionsList = document.createElement('ul');
  suggestionsList.style.cssText = `
    list-style: none;
    padding: 0;
    margin: 0 0 16px 0;
  `;

  for (const suggestion of suggestions) {
    const item = document.createElement('li');
    item.style.cssText = `
      padding: 6px 0;
      font-size: 13px;
      color: #4b5563;
      display: flex;
      align-items: flex-start;
      gap: 8px;
    `;
    item.innerHTML = `
      <span style="flex-shrink: 0; width: 18px; text-align: center; color: #f59e0b">→</span>
      <span>${escapeHtml(suggestion)}</span>
    `;
    suggestionsList.appendChild(item);
  }
  wrapper.appendChild(suggestionsList);

  // Message
  const message = document.createElement('p');
  message.style.cssText = `
    margin: 0 0 16px 0;
    font-size: 12px;
    color: #6b7280;
    line-height: 1.5;
  `;
  message.textContent =
    'Better structured feedback helps our team respond faster and more accurately.';
  wrapper.appendChild(message);

  // Buttons
  const buttonGroup = document.createElement('div');
  buttonGroup.style.cssText = `
    display: flex;
    gap: 10px;
  `;

  const improveBtn = document.createElement('button');
  improveBtn.className = 'fh-quality-gate-improve';
  improveBtn.textContent = '← Go Back & Improve';
  improveBtn.style.cssText = `
    flex: 1;
    padding: 9px 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: white;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 200ms;
  `;
  improveBtn.onmouseover = () => (improveBtn.style.background = '#f9fafb');
  improveBtn.onmouseout = () => (improveBtn.style.background = 'white');
  improveBtn.onclick = () => {
    // Remove inline gate and restore form content
    wrapper.remove();
    for (const child of existingChildren) {
      child.style.display = '';
    }
    onImproveNow();
  };

  const submitBtn = document.createElement('button');
  submitBtn.className = 'fh-quality-gate-submit';
  submitBtn.textContent = 'Submit Anyway →';
  submitBtn.style.cssText = `
    flex: 1;
    padding: 9px 14px;
    border: none;
    border-radius: 6px;
    background: #3b82f6;
    color: white;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 200ms;
  `;
  submitBtn.onmouseover = () => (submitBtn.style.background = '#2563eb');
  submitBtn.onmouseout = () => (submitBtn.style.background = '#3b82f6');
  submitBtn.onclick = () => {
    // Show submitting state instead of removing the wrapper
    wrapper.innerHTML = `
      <div style="text-align:center;padding:24px 0;">
        <div style="font-size:24px;margin-bottom:12px;">⏳</div>
        <p style="margin:0;font-size:14px;color:#4b5563;font-weight:500;">Submitting your feedback...</p>
      </div>
    `;
    onSubmitAnyway();
  };

  buttonGroup.appendChild(improveBtn);
  buttonGroup.appendChild(submitBtn);
  wrapper.appendChild(buttonGroup);

  container.appendChild(wrapper);
}
